import React, { useRef } from 'react';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { Label } from '../Label/Label';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { useAutoExpand } from './useAutoExpand';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { TextAreaParam } from '../../types/TextParam';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValidateTextLength } from '../../utils/useValidateTextLength';

type HTMLTextareaProps = React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>;

type Textarea = {
  autoExpand?: boolean;
  helpText?: string;
  label: string;
  param?: TextAreaParam<string>;
} & Omit<HTMLTextareaProps, 'id'>;

function normalizeParamsToHTMLAttributes(htmlTextAreaProps: HTMLTextareaProps, param?: TextAreaParam<string>) {
  return {
    required: param?.required,
    defaultValue: param?.value,
    name: param?.name,
    maxLength: param?.constraints?.maxLength,
    readOnly: param?.constraints?.readonly,
    ...htmlTextAreaProps,
  };
}

export function Textarea({ autoExpand, helpText, label, param, ...htmlTextAreaProps }: Textarea) {
  const { t } = useSonTranslation('Form');

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const id = useHtmlValidId();
  const textAreaId = `textarea-${id}`;
  const helpTextId = `textarea-help-text-${id}`;

  const htmlAttributes = normalizeParamsToHTMLAttributes(htmlTextAreaProps, param);

  useAutoExpand(autoExpand, textAreaRef);
  useValidateTextLength(textAreaRef);

  return (
    <ElementContainer>
      <Label htmlFor={textAreaId} helpText={helpText} helpTextId={helpTextId} required={htmlAttributes.required}>
        {label}
      </Label>
      <FieldWrapper
        // no inputCounter on readonly inputs
        maxLength={htmlAttributes.readOnly ? undefined : htmlAttributes.maxLength}
        elementRef={textAreaRef}
      >
        <textarea
          id={textAreaId}
          ref={textAreaRef}
          cols={40}
          rows={20}
          className="form2-textarea"
          aria-describedby={helpText && helpTextId}
          onChange={e => {
            // fields can not! only contain white spaces.
            // check for >= 1 for normal validation of fields
            if (e.target.value.length >= 1 && e.target.value?.trim?.() === '') {
              e.target.setCustomValidity(t('fieldCantBeEmpty'));
            } else {
              e.target.setCustomValidity('');
            }
          }}
          {...htmlAttributes}
        />
      </FieldWrapper>
    </ElementContainer>
  );
}
